import { Row } from "@tanstack/react-table";
import { CheckSquare, Delete, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";

import AvatarText from "@/components/reusables/AvatarText";
import { CustomDropdown } from "@/components/reusables/custom-action-button";
import { CustomTable } from "@/components/reusables/data-table/CustomTable";
import { Checkbox } from "@/components/ui/checkbox";
import { SELECTOR_ID } from "@/constants/global-constants";
import { ROUTES } from "@/constants/routes_sidebar/routes-constant";
import { ToolsConstants, ToolsKeys } from "@/constants/tools.constant";
import useGetFilter from "@/hooks/useGetFilter";
import { ResponsePagination } from "@/hooks/usePagination";
import useQueryID from "@/hooks/useQueryId";
import { GlobalRowDataTypes } from "@/interfaces/global-table.interface";
import { ExtendedColumnDef } from "@/interfaces/globals";
import {
  IReturnRequest,
  IReturnRequestItem,
  ProductRequestStatus,
} from "@/interfaces/tools.interface";
import { cn, renderFullName } from "@/lib/utils";
import { dateFormat } from "@/utils/helper-functions/date-time.helper";

interface ReturnRequestTableProps {
  data: IReturnRequest[];
  toggleApproveModal: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      data: IReturnRequestItem[];
    } | null>
  >;
  pagination: ResponsePagination;
  toggleRejectModal: React.Dispatch<
    React.SetStateAction<{
      isOpen: boolean;
      id: string;
      requestBy: string;
    } | null>
  >;
}

const ReturnRequestTable = ({
  data,
  toggleApproveModal,
  pagination,
  toggleRejectModal,
}: ReturnRequestTableProps): JSX.Element => {
  const { set } = useQueryID(ToolsKeys.RETURN_REQUEST_ID);
  const navigate = useNavigate();
  const { projectList } = useGetFilter(["project"]);

  const handleNavigate = (id: string): void => {
    navigate(`${ROUTES.TOOLS}${ROUTES.RETURN_REQUEST}/${id}`);
  };

  const getActionOptions = (
    row: Row<IReturnRequest>,
  ): ActionButtonDataTypes[] => {
    const isAwaiting = row.original.status === ProductRequestStatus.AWAITING;

    return [
      {
        icon: FileText,
        name: "View Products",
        onclick: () => handleNavigate(row.original?.id || ""),
      },
      {
        icon: CheckSquare,
        name: "Approve Request",
        hide: !isAwaiting,
        onclick: (): void => {
          set(row.original?.id);
          toggleApproveModal({
            isOpen: true,
            data: row.original?.returnItems || [],
          });
        },
      },
      {
        icon: Delete,
        name: "Reject Request",
        isTrash: true,
        hide: !isAwaiting,
        onclick: (): void => {
          toggleRejectModal({
            isOpen: true,
            id: row.original?.id || "",
            requestBy: renderFullName({
              params: row.original?.requestedBy,
            }),
          });
        },
      },
    ];
  };

  const columns: ExtendedColumnDef<IReturnRequest>[] = [
    {
      map: {
        key: "id",
        valueFrom: "id",
      },
      id: SELECTOR_ID,
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && "indeterminate")
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label='Select all'
          className={cn(
            "hidden group-hover/header:block",
            table.getIsSomePageRowsSelected() && "block",
            table.getIsAllPageRowsSelected() && "!block",
          )}
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label='Select row'
          onClick={(e) => e.stopPropagation()}
        />
      ),

      size: 12,
      enableSorting: false,
      enableHiding: false,
    },
    {
      map: {
        key: "Project",
        valueFrom: "project.name",
      },
      accessorKey: "Project",
      header: "Project",
      cell: ({ row }) => row.original.project?.name || "-",
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Requested By",
        dataTransformer: (data) =>
          renderFullName({
            params: data.requestedBy,
          }),
      },
      accessorKey: "requested By",
      header: "Requested By",
      cell: ({ row }): JSX.Element => {
        return (
          <AvatarText
            src={row?.original?.requestedBy?.image}
            name={`${renderFullName({
              params: row.original.requestedBy,
            })}`}
            title={`${renderFullName({
              params: row.original.requestedBy,
            })}`}
          />
        );
      },
      meta: {
        minWidth: 200,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Requested At",
        dataTransformer: (data) => dateFormat(data.createdAt, "dd/MM/yyyy"),
      },
      accessorKey: "requested At",
      header: "Requested At",
      cell: ({ row }) => dateFormat(row.original.createdAt, "dd/MM/yyyy"),
      meta: {
        minWidth: 150,
        showRightBorder: true,
      },
    },
    {
      map: {
        key: "Remarks",
        dataTransformer: (data) => data.remarks || "-",
      },
      accessorKey: "remarks",
      header: "Remarks",
      cell: ({ row }) => row.original?.remarks || "-",
      meta: {
        minWidth: 200,
      },
    },
    {
      id: "actions",
      cell: ({ row }): React.JSX.Element => (
        <CustomDropdown data={getActionOptions(row)} />
      ),
      meta: {
        classNameRow: "w-14",
      },
    },
  ];

  return (
    <div>
      <CustomTable
        title={ToolsConstants.INDEX_CONTENT_TEXT.REQUEST}
        columns={columns}
        data={data}
        empty={{}}
        functions={{
          search: ToolsConstants.INDEX_CONTENT_TEXT.REQUEST,
          isExport: true,
          columnToggle: true,
          onRowClick: handleNavigate,
          pagination,
          RightClickData: (
            row: GlobalRowDataTypes<IReturnRequest>,
          ): ActionButtonDataTypes[] =>
            getActionOptions(row as Row<IReturnRequest>),
        }}
        filters={{
          dateRange: true,
          projectId: { label: "Project", options: projectList },
        }}
        hide={{
          filters: true,
          functions: true,
        }}
      />
    </div>
  );
};

export default ReturnRequestTable;
